import { Circle, Group, Layer, Line, Path, Rect, Stage, Text } from "react-konva";
import { useEffect, useMemo, useRef, useState } from "react";
import type { KonvaEventObject } from "konva/lib/Node";
import type { Room, Vec2 } from "../../lib/types";
import { useCoarsePointer, useElementSize } from "../../lib/useViewportSize";
import { useFitToRoom } from "../../lib/useFitToRoom";
import { useCssInsets, type CssInsets } from "../../lib/useCssInsets";
import { cancelKonvaDrags, useStagePanZoom } from "../../lib/useStagePanZoom";
import { SLOP_PX, TOUCH_DRAG_DISTANCE } from "../../lib/touchPickup";
import { pressedTouch, useTouchPickup } from "../../lib/useTouchPickup";
import { HoldHint } from "./HoldHint";
import { annotationScale } from "../../lib/canvasLabels";
import { useInspectorInset } from "../../lib/inspectorInset";
import { useDesign } from "../../lib/store";
import { getEdgePanDelta } from "../../lib/edgePan";
import { Locate } from "lucide-react";
import {
  boundingBox,
  distance,
  formatLength,
  midpoint,
  moveVertex,
  slideEdge,
  snap,
  snapToAxis,
  wallDirection,
} from "../../lib/geom";
import { SketchOverlay, type SketchOutcome } from "./SketchOverlay";
import {
  interiorLockKey,
  interiorSegments,
  moveVertexById,
  slideInteriorSegment,
} from "../../lib/interiorWalls";
import { setCursor } from "../../lib/presence";
import { useAuth } from "../../lib/auth";
import { useCollaborators } from "../../lib/useCollaborators";
import { CollaboratorCursors } from "./CollaboratorCursors";
import { CollaboratorSelections } from "./CollaboratorSelections";
import { CollaboratorDragPreviewsBuild } from "./CollaboratorDragPreviews";
import { gridStepsFor } from "../../lib/gridStep";
import { GridLegend } from "./GridLegend";

/** SVG path for the lock icon rendered next to a locked wall's length label.
 *  Hand-traced from `lucide-react`'s `Lock` glyph (24×24 viewBox, sized down to
 *  10px at render time) so we don't need a DOM-only `<svg>` inside the Konva
 *  stage. */
const LOCK_ICON_PATH =
  "M5 11h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2zm2 0V7a5 5 0 0 1 10 0v4";

/** Stable empty-locks reference for the `lockedWallLengths` selector so a
 *  missing field doesn't return a fresh `{}` on every render (which would
 *  trip React 19's `useSyncExternalStore` snapshot equality check). */
const EMPTY_LOCKS: Record<number, number> = Object.freeze({});

/** "Every foot" lines — always rendered as the dominant reference even when
 *  the adaptive grid below subdivides further at high zoom levels. */
const MAJOR_GRID_INCHES = 12;
/** Grid step that wall slides and vertex drags round to. A finer step than
 *  the visible adaptive grid is intentional: the visible grid is a *reference*
 *  for the eye that crossfades with zoom, while drag snap is a *commitment* —
 *  users expect to be able to nudge a wall by an inch even at the coarsest
 *  zoom. Square-corner bias is preserved by {@link AXIS_SNAP_INCHES} below,
 *  which runs before this grid snap. */
const SNAP_INCHES = 1;
/** Tolerance (inches) for snapping a dragged vertex's x/y onto another
 *  vertex's coordinate — the source of the "rooms want rectilinear corners"
 *  feel. Runs before grid snap; deliberately larger than {@link SNAP_INCHES}
 *  so the axis lock wins over the finer grid step. */
const AXIS_SNAP_INCHES = 6;

// Grid color tiers, sorted darkest → lightest. Match the palette used in
// RoomCanvas so the editor and the design canvas read as the same surface.
const GRID_MAJOR_STROKE = "#bcc0c8";
const GRID_COARSE_STROKE = "#dadde2";
const GRID_FINE_STROKE = "#eef0f3";

/** Visual constants. Mobile-friendly touch targets. */
const WALL_THICKNESS_PX = 8;
const WALL_HIT_PX = 36;
const VERTEX_RADIUS_PX = 11;
const VERTEX_HIT_PX = 28;
const ACCENT = "#2a70f5";

type DragKind =
  | { kind: "wall"; index: number; originPxX: number; originPxY: number; normX: number; normY: number; startPoints: Vec2[] }
  | { kind: "vertex"; index: number; startPoints: Vec2[]; originPxX: number; originPxY: number }
  | {
      kind: "interior-wall";
      wallId: string;
      segment: number;
      originPxX: number;
      originPxY: number;
      normX: number;
      normY: number;
      startRoom: Room;
    }
  | {
      kind: "interior-vertex";
      vertexId: string;
      origin: Vec2;
      originPxX: number;
      originPxY: number;
      startRoom: Room;
    };

interface Props {
  /** Hide the floating area/perimeter pill so the parent can render its own. */
  hideAreaBadge?: boolean;
  /** Active build tool. Defaults to "edit" (wall/vertex drag).
   *  When `"sketch"`, hit targets are disabled and the SketchOverlay layer
   *  takes over pointer input. */
  tool?: "edit" | "sketch";
  /** Sketch is classified as an addition or an interior wall. */
  extendMode?: boolean;
  /** When false, `previewPoints` is an open polyline (an interior wall). */
  previewClosed?: boolean;
  /** Called by the Sketch tool when a stroke is processed. Only consulted
   *  when `tool === "sketch"`. */
  onSketchResult?: (outcome: SketchOutcome) => void;
  /** Optional preview polygon rendered above the room with a dashed style,
   *  used by the Sketch tool to show the cleaned ghost awaiting confirm. */
  previewPoints?: Vec2[] | null;
  /** Chrome floating over the canvas edges (CSS lengths); the room fits
   *  inside what's left. */
  fitInsets?: CssInsets;
}

export function RoomEditorCanvas({
  hideAreaBadge = false,
  tool = "edit",
  extendMode = false,
  previewClosed = true,
  onSketchResult,
  previewPoints = null,
  fitInsets,
}: Props) {
  const [containerRef, size] = useElementSize<HTMLDivElement>();
  const room = useDesign((s) => s.design.room);
  const openings = useDesign((s) => s.design.openings);
  const mounts = useDesign((s) => s.design.mounts);
  const unit = useDesign((s) => s.design.unit);
  const selectedWall = useDesign((s) => s.selectedRoomWallIndex);
  const selectedVertex = useDesign((s) => s.selectedRoomVertexIndex);
  const selectRoomWall = useDesign((s) => s.selectRoomWall);
  const selectRoomVertex = useDesign((s) => s.selectRoomVertex);
  const selectedInteriorWall = useDesign((s) => s.selectedInteriorWall);
  const selectedInteriorVertexId = useDesign((s) => s.selectedInteriorVertexId);
  const selectInteriorWall = useDesign((s) => s.selectInteriorWall);
  const selectInteriorVertex = useDesign((s) => s.selectInteriorVertex);
  const beginGesture = useDesign((s) => s.beginGesture);
  const endGesture = useDesign((s) => s.endGesture);
  const cancelGesture = useDesign((s) => s.cancelGesture);
  const setRoomPointsLive = useDesign((s) => s.setRoomPointsLive);
  const setRoomLive = useDesign((s) => s.setRoomLive);
  const lockedWallLengths = useDesign(
    (s) => s.design.room.lockedWallLengths ?? EMPTY_LOCKS
  );
  const syncWallLocksToCurrent = useDesign((s) => s.syncWallLocksToCurrent);
  const sharedRoomId = useDesign((s) => s.activeSharedRoomId);
  const auth = useAuth();
  const collaborators = useCollaborators(sharedRoomId, auth.user?.uid ?? null);

  const points = room.points;
  const [fitBasisPoints, setFitBasisPoints] = useState(() => points.map((p) => ({ ...p })));
  const dragRef = useRef<DragKind | null>(null);
  const dragMovedRef = useRef(false);
  const lastCanvasPointerRef = useRef<Vec2 | null>(null);
  const lastScenePointerRef = useRef<Vec2 | null>(null);
  const edgePanFrameRef = useRef<number | null>(null);
  const suppressFitSyncForPointsRef = useRef<Vec2[] | null>(null);
  /** Tracks shift-key state across all input sources (pointer events,
   *  keyboard) so vertex drags can constrain to the dominant motion axis
   *  even between pointer-move events. */
  const shiftHeldRef = useRef(false);
  /** Tracks space-key state. The ref is read inside Konva drag handlers
   *  (which fire outside React renders); the state drives Stage `draggable`
   *  and the SketchOverlay's pan-mode prop. */
  const spaceHeldRef = useRef(false);
  const [spaceHeld, setSpaceHeld] = useState(false);
  // Re-render whenever the drag state flips so highlights update.
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    return () => {
      if (edgePanFrameRef.current != null) {
        window.cancelAnimationFrame(edgePanFrameRef.current);
      }
    };
  }, []);

  // Listen for shift-key changes so the axis-lock kicks in/out instantly,
  // even if the user is holding the pointer still. We re-apply the current
  // drag with the new shift state so the preview snaps to the locked axis
  // without the user needing to wiggle the pointer first.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Shift") return;
      if (shiftHeldRef.current) return;
      shiftHeldRef.current = true;
      const ds = dragRef.current;
      const p = lastScenePointerRef.current;
      if (ds && p) applyDragAtPointer(ds, p);
    }
    function onKeyUp(e: KeyboardEvent) {
      if (e.key !== "Shift") return;
      if (!shiftHeldRef.current) return;
      shiftHeldRef.current = false;
      const ds = dragRef.current;
      const p = lastScenePointerRef.current;
      if (ds && p) applyDragAtPointer(ds, p);
    }
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
    // applyDragAtPointer is defined below and closes over fresh state via
    // refs/store reads; listeners only depend on stable refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Space-to-pan: Figma/Photoshop-style hold-Space-and-drag pan, primarily
  // for sketch mode where the SketchOverlay otherwise eats every pointer
  // event. We track the key globally so the user can press it before
  // entering the canvas, but defer to focused buttons/inputs (Space is
  // their activation key) and bare modifier combos.
  useEffect(() => {
    function isSpaceEventForUs(e: KeyboardEvent): boolean {
      if (e.code !== "Space" && e.key !== " ") return false;
      if (e.metaKey || e.ctrlKey || e.altKey) return false;
      const t = e.target;
      if (t instanceof HTMLElement) {
        const tag = t.tagName;
        if (
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          tag === "SELECT" ||
          tag === "BUTTON"
        ) return false;
        if (t.isContentEditable) return false;
        if (t.getAttribute("role") === "button") return false;
      }
      return true;
    }
    function onKeyDown(e: KeyboardEvent) {
      if (!isSpaceEventForUs(e)) return;
      // Suppress page scroll even on key-repeat so the canvas stays put.
      e.preventDefault();
      if (spaceHeldRef.current) return;
      spaceHeldRef.current = true;
      setSpaceHeld(true);
    }
    function onKeyUp(e: KeyboardEvent) {
      if (e.code !== "Space" && e.key !== " ") return;
      if (!spaceHeldRef.current) return;
      spaceHeldRef.current = false;
      setSpaceHeld(false);
    }
    function releaseSpace() {
      if (!spaceHeldRef.current) return;
      spaceHeldRef.current = false;
      setSpaceHeld(false);
    }
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    // Drop the held state if the window loses focus or the tab is hidden;
    // otherwise releasing Space outside the page would leave us stuck in
    // pan mode.
    window.addEventListener("blur", releaseSpace);
    document.addEventListener("visibilitychange", releaseSpace);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", releaseSpace);
      document.removeEventListener("visibilitychange", releaseSpace);
    };
  }, []);

  // Keep the editor's base scale stable during direct manipulation; otherwise
  // expanding the room repeatedly refits the polygon and feels like zooming out.
  useEffect(() => {
    if (suppressFitSyncForPointsRef.current === points) return;
    if (!dragRef.current) setFitBasisPoints(points.map((p) => ({ ...p })));
  }, [points]);

  // We give the editor extra breathing room (vertex handles can stick out past the bounds).
  const chrome = useCssInsets(fitInsets);
  const fit = useFitToRoom(fitBasisPoints, size.width, size.height, 56, undefined, chrome.insets);
  const inspector = useInspectorInset();
  const panZoom = useStagePanZoom({ onInterrupt: () => cancelLocalDrag() });
  const coarse = useCoarsePointer();
  const pickup = useTouchPickup();
  /** Where a finger grabbed an already-selected wall or corner. It only
   *  starts moving once the finger clears {@link TOUCH_DRAG_DISTANCE}, so a
   *  tap never nudges it. */
  const touchDragStartRef = useRef<Vec2 | null>(null);

  // ---- Drag handlers (manual, work on mouse + touch via Konva's normalized events) ----

  /**
   * Returns the pointer in the Stage's pre-transform ("scene") coords so that
   * our hand-rolled drag math still works regardless of zoom/pan.
   *
   * Also captures the event's `shiftKey` into `shiftHeldRef` so keyboard
   * listeners and pointer-driven calls stay in sync without an extra arg
   * threaded through every drag helper.
   */
  function getPointer(e: KonvaEventObject<unknown>): Vec2 | null {
    const stage = e.target.getStage();
    if (!stage) return null;
    lastCanvasPointerRef.current = stage.getPointerPosition();
    const native = e.evt as { shiftKey?: boolean } | undefined;
    if (native && typeof native.shiftKey === "boolean") {
      shiftHeldRef.current = native.shiftKey;
    }
    const scene = stage.getRelativePointerPosition();
    if (scene) lastScenePointerRef.current = scene;
    return scene;
  }

  function stopEdgePan() {
    if (edgePanFrameRef.current != null) {
      window.cancelAnimationFrame(edgePanFrameRef.current);
      edgePanFrameRef.current = null;
    }
    lastCanvasPointerRef.current = null;
  }

  function applyDragAtPointer(ds: DragKind, p: Vec2) {
    if (ds.kind === "wall") {
      const dxPx = p.x - ds.originPxX;
      const dyPx = p.y - ds.originPxY;
      const dxIn = dxPx / fit.scale;
      const dyIn = dyPx / fit.scale;
      const delta = dxIn * ds.normX + dyIn * ds.normY;
      const snapped = snap(delta, SNAP_INCHES);
      if (Math.abs(snapped) < 0.001) return; // no real movement yet
      if (!dragMovedRef.current) {
        dragMovedRef.current = true;
      }
      const newPoints = slideEdge(ds.startPoints, ds.index, snapped);
      suppressFitSyncForPointsRef.current = newPoints;
      setRoomPointsLive(newPoints);
    } else if (ds.kind === "vertex") {
      const orig = ds.startPoints[ds.index];
      let targetRoom = fit.pxToRoom(p);

      // Shift held → lock the move to the dominant axis from the drag origin.
      // Compare absolute pixel deltas so the axis decision is independent of
      // fit scale; the locked axis stays pinned to its origin coordinate.
      // This deliberately runs *before* grid/axis snap so the lock is exact
      // even when grid-snap would have nudged you off the locked axis.
      if (shiftHeldRef.current) {
        const dxPx = Math.abs(p.x - ds.originPxX);
        const dyPx = Math.abs(p.y - ds.originPxY);
        if (dxPx >= dyPx) {
          targetRoom = { x: targetRoom.x, y: orig.y };
        } else {
          targetRoom = { x: orig.x, y: targetRoom.y };
        }
      }

      const others = ds.startPoints.filter((_, idx) => idx !== ds.index);
      const ax = snapToAxis(targetRoom, others, AXIS_SNAP_INCHES);
      let snapped: Vec2 = { x: snap(ax.pos.x, SNAP_INCHES), y: snap(ax.pos.y, SNAP_INCHES) };

      // Re-clamp the locked axis after grid snap so 3" rounding can't undo
      // the user's "stay exactly on this axis" intent.
      if (shiftHeldRef.current) {
        const dxPx = Math.abs(p.x - ds.originPxX);
        const dyPx = Math.abs(p.y - ds.originPxY);
        if (dxPx >= dyPx) {
          snapped = { x: snapped.x, y: orig.y };
        } else {
          snapped = { x: orig.x, y: snapped.y };
        }
      }

      if (Math.abs(snapped.x - orig.x) < 0.5 && Math.abs(snapped.y - orig.y) < 0.5) return;
      if (!dragMovedRef.current) {
        dragMovedRef.current = true;
      }
      const newPoints = moveVertex(ds.startPoints, ds.index, snapped);
      suppressFitSyncForPointsRef.current = newPoints;
      setRoomPointsLive(newPoints);
    } else if (ds.kind === "interior-wall") {
      const dxPx = p.x - ds.originPxX;
      const dyPx = p.y - ds.originPxY;
      const dxIn = dxPx / fit.scale;
      const dyIn = dyPx / fit.scale;
      const delta = dxIn * ds.normX + dyIn * ds.normY;
      const snapped = snap(delta, SNAP_INCHES);
      if (Math.abs(snapped) < 0.001) return;
      dragMovedRef.current = true;
      setRoomLive(slideInteriorSegment(ds.startRoom, ds.wallId, ds.segment, snapped));
    } else if (ds.kind === "interior-vertex") {
      let targetRoom = fit.pxToRoom(p);
      if (shiftHeldRef.current) {
        const dxPx = Math.abs(p.x - ds.originPxX);
        const dyPx = Math.abs(p.y - ds.originPxY);
        if (dxPx >= dyPx) targetRoom = { x: targetRoom.x, y: ds.origin.y };
        else targetRoom = { x: ds.origin.x, y: targetRoom.y };
      }
      const ax = snapToAxis(targetRoom, [], AXIS_SNAP_INCHES);
      let snapped: Vec2 = { x: snap(ax.pos.x, SNAP_INCHES), y: snap(ax.pos.y, SNAP_INCHES) };
      if (shiftHeldRef.current) {
        const dxPx = Math.abs(p.x - ds.originPxX);
        const dyPx = Math.abs(p.y - ds.originPxY);
        if (dxPx >= dyPx) snapped = { x: snapped.x, y: ds.origin.y };
        else snapped = { x: ds.origin.x, y: snapped.y };
      }
      if (Math.abs(snapped.x - ds.origin.x) < 0.5 && Math.abs(snapped.y - ds.origin.y) < 0.5) return;
      dragMovedRef.current = true;
      setRoomLive(moveVertexById(ds.startRoom, ds.vertexId, snapped));
    }
  }

  function startEdgePan() {
    if (edgePanFrameRef.current != null) return;
    const tick = () => {
      edgePanFrameRef.current = null;
      const ds = dragRef.current;
      const pointer = lastCanvasPointerRef.current;
      if (!ds || !pointer) return;

      const delta = getEdgePanDelta(pointer, size);
      if (Math.abs(delta.x) > 0.01 || Math.abs(delta.y) > 0.01) {
        panZoom.panBy(delta.x, delta.y);
        const p = panZoom.stageRef.current?.getRelativePointerPosition();
        if (p) applyDragAtPointer(ds, p);
      }

      edgePanFrameRef.current = window.requestAnimationFrame(tick);
    };
    edgePanFrameRef.current = window.requestAnimationFrame(tick);
  }

  // Keep the selected wall or corner out from under the details sheet.
  useEffect(() => {
    if (inspector.bottom <= 0 && inspector.right <= 0) return;
    const el = containerRef.current;
    if (!el) return;
    let target: Vec2[] | null = null;
    if (selectedVertex != null && points[selectedVertex]) target = [points[selectedVertex]];
    else if (selectedWall != null && points[selectedWall]) {
      target = [points[selectedWall], points[(selectedWall + 1) % points.length]];
    }
    if (!target) return;
    const box = boundingBox(target.map((p) => fit.pointToPx(p)));
    const pad = VERTEX_HIT_PX / Math.max(panZoom.scale, 1e-6);
    const r = el.getBoundingClientRect();
    panZoom.ensureVisible(
      { x: box.minX - pad, y: box.minY - pad, width: box.width + pad * 2, height: box.height + pad * 2 },
      {
        top: chrome.insets.top,
        left: chrome.insets.left,
        bottom: Math.max(chrome.insets.bottom, inspector.bottom - (window.innerHeight - r.bottom)),
        right: Math.max(chrome.insets.right, inspector.right - (window.innerWidth - r.right)),
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWall, selectedVertex, inspector.bottom, inspector.right]);

  /** A second finger landed mid-drag: put the geometry back, no undo entry. */
  function cancelLocalDrag() {
    pickup.disarm();
    touchDragStartRef.current = null;
    if (dragRef.current) {
      dragRef.current = null;
      dragMovedRef.current = false;
      stopEdgePan();
      cancelGesture();
      forceUpdate((x) => x + 1);
    }
  }

  /** A press that should start (or keep) a drag. Extra fingers belong to the
   *  pinch handler, so they bubble to the Stage instead. */
  function canStartDrag(e: KonvaEventObject<unknown>): boolean {
    if (dragRef.current) return false;
    const touches = (e.evt as TouchEvent | undefined)?.touches;
    return !touches || touches.length < 2;
  }

  /**
   * Route a press on a wall, corner, or length label. Mouse, pen, and the
   * selected target drag straight away; on touch anything else needs a hold
   * (see `touchPickup.ts`), so a swipe that starts on it pans instead.
   */
  function pressTarget(
    e: KonvaEventObject<unknown>,
    target: { key: string; selected: boolean; select: () => void },
    start: (e: KonvaEventObject<unknown>) => void
  ) {
    const touch = pressedTouch(e);
    if (!coarse || !touch || target.selected) {
      start(e);
      if (touch && dragRef.current) {
        touchDragStartRef.current = { x: touch.clientX, y: touch.clientY };
      }
      return;
    }
    if (!canStartDrag(e)) return;
    pickup.arm(target.key, touch, {
      onPickup: () => {
        const stage = e.target.getStage();
        if (stage) cancelKonvaDrags(stage);
        start(e);
        pickup.drop();
      },
      onTap: target.select,
    });
  }

  function startWallDrag(e: KonvaEventObject<unknown>, i: number) {
    if (!canStartDrag(e)) return;
    e.cancelBubble = true;
    const p = getPointer(e);
    if (!p) return;
    const a = points[i];
    const b = points[(i + 1) % points.length];
    const dir = wallDirection(a, b);
    dragRef.current = {
      kind: "wall",
      index: i,
      originPxX: p.x,
      originPxY: p.y,
      normX: -dir.y,
      normY: dir.x,
      startPoints: points.map((q) => ({ ...q })),
    };
    dragMovedRef.current = false;
    forceUpdate((x) => x + 1);
    beginGesture({ blockUndo: true });
  }

  function startInteriorWallDrag(
    e: KonvaEventObject<unknown>,
    wallId: string,
    segment: number,
    a: Vec2,
    b: Vec2
  ) {
    if (!canStartDrag(e)) return;
    e.cancelBubble = true;
    const p = getPointer(e);
    if (!p) return;
    const dir = wallDirection(a, b);
    dragRef.current = {
      kind: "interior-wall",
      wallId,
      segment,
      originPxX: p.x,
      originPxY: p.y,
      normX: -dir.y,
      normY: dir.x,
      startRoom: room,
    };
    dragMovedRef.current = false;
    forceUpdate((x) => x + 1);
    beginGesture({ blockUndo: true });
  }

  function startInteriorVertexDrag(e: KonvaEventObject<unknown>, vertexId: string, origin: Vec2) {
    if (!canStartDrag(e)) return;
    e.cancelBubble = true;
    const p = getPointer(e);
    dragRef.current = {
      kind: "interior-vertex",
      vertexId,
      origin,
      originPxX: p?.x ?? fit.pointToPx(origin).x,
      originPxY: p?.y ?? fit.pointToPx(origin).y,
      startRoom: room,
    };
    dragMovedRef.current = false;
    forceUpdate((x) => x + 1);
    beginGesture({ blockUndo: true });
  }

  function startVertexDrag(e: KonvaEventObject<unknown>, i: number) {
    if (!canStartDrag(e)) return;
    e.cancelBubble = true;
    const p = getPointer(e);
    dragRef.current = {
      kind: "vertex",
      index: i,
      startPoints: points.map((q) => ({ ...q })),
      // Origin in scene-pixel coords lets us compare drag distance on each
      // axis (for Shift axis-lock) using the same units we read from
      // pointer events. Falls back to the vertex's own pixel position if
      // for some reason `getPointer` couldn't resolve a stage pointer.
      originPxX: p?.x ?? fit.pointToPx(points[i]).x,
      originPxY: p?.y ?? fit.pointToPx(points[i]).y,
    };
    dragMovedRef.current = false;
    forceUpdate((x) => x + 1);
    beginGesture({ blockUndo: true });
  }

  function broadcastBuildCursorAt(p: Vec2 | null) {
    if (!sharedRoomId || !p) return;
    const roomPos = fit.pxToRoom(p);
    setCursor({
      view: "build",
      wallIndex: null,
      x: roomPos.x,
      y: roomPos.y,
    });
  }

  function onStagePointerMove(e: KonvaEventObject<unknown>) {
    // If the user added a second finger, hand off to the pinch handler.
    const evt = e.evt as { touches?: TouchList } | undefined;
    if (evt && evt.touches && evt.touches.length > 1) {
      cancelLocalDrag();
      return;
    }
    const ds = dragRef.current;
    const p = getPointer(e);
    broadcastBuildCursorAt(p);
    if (!ds) return;
    if (!p) return;
    const grabbed = touchDragStartRef.current;
    if (grabbed) {
      const t = (e.evt as TouchEvent | undefined)?.touches?.[0];
      if (t && Math.hypot(t.clientX - grabbed.x, t.clientY - grabbed.y) < TOUCH_DRAG_DISTANCE) return;
      touchDragStartRef.current = null;
    }
    startEdgePan();
    applyDragAtPointer(ds, p);
  }

  function onStageDragMoveBroadcast(e: KonvaEventObject<DragEvent>) {
    if (!sharedRoomId) return;
    const stage = e.target.getStage();
    if (!stage) return;
    broadcastBuildCursorAt(stage.getRelativePointerPosition());
  }

  function onStageWheelBroadcast(e: KonvaEventObject<WheelEvent>) {
    if (!sharedRoomId) return;
    const stage = e.target.getStage();
    if (!stage) return;
    broadcastBuildCursorAt(stage.getRelativePointerPosition());
  }

  function onStageMouseLeaveBroadcast() {
    if (sharedRoomId) setCursor(null);
  }

  function onStagePointerUp(e: KonvaEventObject<unknown>) {
    const ds = dragRef.current;
    if (!ds) return;
    touchDragStartRef.current = null;
    const movedSomething = dragMovedRef.current;
    if (ds.kind === "wall") selectRoomWall(ds.index);
    else if (ds.kind === "vertex") selectRoomVertex(ds.index);
    else if (ds.kind === "interior-wall") {
      selectInteriorWall({ wallId: ds.wallId, segment: ds.segment });
    } else {
      selectInteriorVertex(ds.vertexId);
    }
    dragRef.current = null;
    dragMovedRef.current = false;
    stopEdgePan();
    // After a drag that actually committed geometry, refresh every wall
    // lock's stored length to whatever the polygon now shows. This keeps
    // the lock state "sticky" (the user-marked walls remain locked) while
    // honoring the new shape they explicitly dragged into.
    if (movedSomething) syncWallLocksToCurrent();
    endGesture();
    forceUpdate((x) => x + 1);
    e.cancelBubble = true;
  }

  function onStageBackgroundTap(e: KonvaEventObject<unknown>) {
    if (dragRef.current) return; // ignore if dragging
    if (e.target === e.target.getStage()) {
      selectRoomWall(null);
      selectRoomVertex(null);
      selectInteriorWall(null);
      selectInteriorVertex(null);
    }
  }

  // ---- Computed visuals ----

  const flatPolygon = useMemo(() => {
    const arr: number[] = [];
    for (const p of points) {
      const pp = fit.pointToPx(p);
      arr.push(pp.x, pp.y);
    }
    return arr;
  }, [points, fit]);

  /** Map current zoom to a coarse/fine grid step pair. The grid responds to
   *  zoom: 1' lines at the furthest zoom, 1" lines at the closest, with clean
   *  intermediate stops ([12,6,3,1]) crossfading between bands. */
  const gridStep = useMemo(() => gridStepsFor(panZoom.scale), [panZoom.scale]);
  /** Scale for the wall-length pills — see `canvasLabels.ts`. */
  const labelK = annotationScale(panZoom.scale);

  const gridLines = useMemo(() => {
    const empty = {
      major: [] as number[][],
      coarse: [] as number[][],
      fine: [] as number[][],
    };
    if (
      fit.scale === 0 ||
      !Number.isFinite(fit.scale) ||
      panZoom.scale === 0 ||
      !Number.isFinite(panZoom.scale) ||
      !Number.isFinite(panZoom.x) ||
      !Number.isFinite(panZoom.y) ||
      size.width === 0 ||
      size.height === 0
    ) {
      return empty;
    }
    // Visible viewport in stage-local pixels: invert the Stage transform
    // (scale + (panZoom.x, panZoom.y) translation) on the screen-space rect
    // [0,size.width] × [0,size.height]. fit.pxToRoom expects stage-local px.
    const stageLeft = -panZoom.x / panZoom.scale;
    const stageTop = -panZoom.y / panZoom.scale;
    const stageRight = (size.width - panZoom.x) / panZoom.scale;
    const stageBottom = (size.height - panZoom.y) / panZoom.scale;
    const tl = fit.pxToRoom({ x: stageLeft, y: stageTop });
    const br = fit.pxToRoom({ x: stageRight, y: stageBottom });
    const viewMinX = Math.min(tl.x, br.x);
    const viewMinY = Math.min(tl.y, br.y);
    const viewMaxX = Math.max(tl.x, br.x);
    const viewMaxY = Math.max(tl.y, br.y);
    // One full viewport of buffer on every side keeps the grid covered while
    // the user drag-pans — Konva moves the Stage imperatively and only syncs
    // (panZoom.x, panZoom.y) back to React on dragend.
    const padX = viewMaxX - viewMinX;
    const padY = viewMaxY - viewMinY;
    const minX = viewMinX - padX;
    const minY = viewMinY - padY;
    const maxX = viewMaxX + padX;
    const maxY = viewMaxY + padY;
    // Iterate at the finest step and bucket each line into its tier. Steps
    // are integer divisors of each other (enforced by `gridStepsFor`), so any
    // line at a fineStep multiple that's also a coarseStep multiple lands on
    // the same pixel, and the line is emitted exactly once into the heaviest
    // applicable tier — making the crossfade between bands seam-free.
    const major: number[][] = [];
    const coarse: number[][] = [];
    const fine: number[][] = [];
    const fineStep = gridStep.fine;
    const coarseStep = gridStep.coarse;
    if (fineStep <= 0 || !Number.isFinite(fineStep)) return empty;
    const startX = Math.floor(minX / fineStep) * fineStep;
    const startY = Math.floor(minY / fineStep) * fineStep;
    if (
      !Number.isFinite(startX) ||
      !Number.isFinite(startY) ||
      !Number.isFinite(maxX) ||
      !Number.isFinite(maxY)
    ) {
      return empty;
    }
    // Hard cap on iteration count — defense-in-depth so a future bug in
    // the bounds math (e.g. a malformed import that leaves the room
    // polygon at a 10,000ft scale) can never lock the page. Mirrors the
    // identical guard in `RoomCanvas.tsx > buildAdaptiveGrid`.
    const MAX_LINES = 10000;
    const endX = Math.min(maxX, startX + MAX_LINES * fineStep);
    const endY = Math.min(maxY, startY + MAX_LINES * fineStep);
    for (let x = startX; x <= endX; x += fineStep) {
      const p1 = fit.pointToPx({ x, y: minY });
      const p2 = fit.pointToPx({ x, y: maxY });
      const isMajor = Math.abs(x % MAJOR_GRID_INCHES) < 1e-6;
      const isCoarse = !isMajor && Math.abs(x % coarseStep) < 1e-6;
      const seg: number[] = [p1.x, p1.y, p2.x, p2.y];
      if (isMajor) major.push(seg);
      else if (isCoarse) coarse.push(seg);
      else fine.push(seg);
    }
    for (let y = startY; y <= endY; y += fineStep) {
      const p1 = fit.pointToPx({ x: minX, y });
      const p2 = fit.pointToPx({ x: maxX, y });
      const isMajor = Math.abs(y % MAJOR_GRID_INCHES) < 1e-6;
      const isCoarse = !isMajor && Math.abs(y % coarseStep) < 1e-6;
      const seg: number[] = [p1.x, p1.y, p2.x, p2.y];
      if (isMajor) major.push(seg);
      else if (isCoarse) coarse.push(seg);
      else fine.push(seg);
    }
    return { major, coarse, fine };
  }, [
    fit,
    panZoom.x,
    panZoom.y,
    panZoom.scale,
    size.height,
    size.width,
    gridStep.coarse,
    gridStep.fine,
  ]);

  const interior = useMemo(() => interiorSegments(room), [room]);
  const interiorOnlyVertices = useMemo(() => {
    const ids = new Set(room.pointIds ?? []);
    return (room.interiorVertices ?? []).filter((v) => !ids.has(v.id));
  }, [room.interiorVertices, room.pointIds]);

  const wallSegments = useMemo(() => {
    return points.map((p, i) => {
      const next = points[(i + 1) % points.length];
      return {
        index: i,
        a: p,
        b: next,
        mid: midpoint(p, next),
        len: distance(p, next),
      };
    });
  }, [points]);

  const hasRoomShape = points.length >= 3;
  const dragging = dragRef.current;
  const sketchActive = tool === "sketch";
  const panMode = sketchActive && spaceHeld;

  // Reflect Space-hold immediately on the stage container so the cursor
  // becomes a grabber even before the user starts dragging. We only own the
  // cursor while in sketch mode; in edit mode the per-element hover handlers
  // already manage it.
  useEffect(() => {
    const stage = panZoom.stageRef.current;
    if (!stage) return;
    const container = stage.container();
    if (sketchActive) {
      container.style.cursor = spaceHeld ? "grab" : "default";
    }
  }, [sketchActive, spaceHeld, panZoom.stageRef]);
  const previewFlat = useMemo(() => {
    const minPts = previewClosed ? 3 : 2;
    if (!previewPoints || previewPoints.length < minPts) return null;
    const arr: number[] = [];
    for (const p of previewPoints) {
      const pp = fit.pointToPx(p);
      arr.push(pp.x, pp.y);
    }
    return arr;
  }, [previewPoints, previewClosed, fit]);

  return (
    <div ref={containerRef} className="absolute inset-0 select-none touch-none">
      {chrome.probes}
      {size.width > 0 && size.height > 0 && (
        <Stage
          ref={panZoom.stageRef}
          width={size.width}
          height={size.height}
          scaleX={panZoom.scale}
          scaleY={panZoom.scale}
          x={panZoom.x}
          y={panZoom.y}
          draggable={sketchActive ? spaceHeld : !dragging}
          dragDistance={coarse ? SLOP_PX : undefined}
          onDragStart={(e) => {
            if (!spaceHeldRef.current) return;
            const stage = e.target.getStage();
            if (stage) stage.container().style.cursor = "grabbing";
          }}
          onDragEnd={(e) => {
            panZoom.onDragEnd(e);
            const stage = e.target.getStage();
            if (stage && sketchActive) {
              stage.container().style.cursor = spaceHeldRef.current
                ? "grab"
                : "default";
            }
          }}
          onDragMove={onStageDragMoveBroadcast}
          onWheel={(e) => {
            panZoom.onWheel(e);
            onStageWheelBroadcast(e);
          }}
          onClick={onStageBackgroundTap}
          onTap={onStageBackgroundTap}
          onMouseMove={onStagePointerMove}
          onMouseUp={onStagePointerUp}
          onMouseLeave={(e) => {
            onStagePointerUp(e);
            onStageMouseLeaveBroadcast();
          }}
          onTouchStart={panZoom.onTouchStart}
          onTouchMove={(e) => {
            // Two-finger pinch wins over any in-progress wall/vertex drag.
            if (e.evt.touches && e.evt.touches.length > 1) {
              cancelLocalDrag();
              panZoom.onTouchMove(e);
              return;
            }
            onStagePointerMove(e);
          }}
          onTouchEnd={(e) => {
            panZoom.onTouchEnd(e);
            onStagePointerUp(e);
          }}
        >
          {/* Grid: three-tier adaptive layer.
              - fine   → next-finer subdivision, fades in via gridStep.fineOpacity
              - coarse → currently dominant subdivision, full opacity
              - major  → every-foot reference lines, always full opacity */}
          <Layer listening={false}>
            {gridLines.fine.map((pts, idx) => (
              <Line
                key={`f-${idx}`}
                points={pts}
                stroke={GRID_FINE_STROKE}
                strokeWidth={1}
                opacity={gridStep.fineOpacity}
              />
            ))}
            {gridLines.coarse.map((pts, idx) => (
              <Line
                key={`c-${idx}`}
                points={pts}
                stroke={GRID_COARSE_STROKE}
                strokeWidth={1}
              />
            ))}
            {gridLines.major.map((pts, idx) => (
              <Line
                key={`M-${idx}`}
                points={pts}
                stroke={GRID_MAJOR_STROKE}
                strokeWidth={1}
              />
            ))}
          </Layer>

          {/* Room polygon */}
          <Layer listening={!sketchActive && hasRoomShape}>
            {hasRoomShape && (
            <Line
              points={flatPolygon}
              closed
              fill="#ffffff"
              stroke="#0c0c10"
              strokeWidth={WALL_THICKNESS_PX}
              lineJoin="round"
              shadowColor="#000"
              shadowBlur={20}
              shadowOpacity={0.06}
              shadowOffsetY={4}
              listening={false}
            />
            )}

            {/* Wall hit + highlight overlays */}
            {hasRoomShape && wallSegments.map((w) => {
              const a = fit.pointToPx(w.a);
              const b = fit.pointToPx(w.b);
              const isSelected = selectedWall === w.index;
              const isDragging = dragging?.kind === "wall" && dragging.index === w.index;
              return (
                <Line
                  key={`wall-${w.index}`}
                  points={[a.x, a.y, b.x, b.y]}
                  stroke={isSelected || isDragging ? ACCENT : "rgba(0,0,0,0.001)"}
                  strokeWidth={isSelected || isDragging ? WALL_THICKNESS_PX + 4 : WALL_THICKNESS_PX}
                  hitStrokeWidth={WALL_HIT_PX}
                  lineCap="round"
                  opacity={isSelected || isDragging ? 0.55 : 1}
                  onMouseDown={(e) => startWallDrag(e, w.index)}
                  onTouchStart={(e) =>
                    pressTarget(
                      e,
                      { key: `wall-${w.index}`, selected: isSelected, select: () => selectRoomWall(w.index) },
                      (ev) => startWallDrag(ev, w.index)
                    )
                  }
                  onMouseEnter={(e) => {
                    const stage = e.target.getStage();
                    if (stage) stage.container().style.cursor = "grab";
                  }}
                  onMouseLeave={(e) => {
                    const stage = e.target.getStage();
                    if (stage && !dragRef.current) stage.container().style.cursor = "default";
                  }}
                />
              );
            })}

            {/* Wall length labels (tappable) */}
            {hasRoomShape && wallSegments.map((w) => {
              const m = fit.pointToPx(w.mid);
              const a = fit.pointToPx(w.a);
              const b = fit.pointToPx(w.b);
              const angle = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
              // Keep labels upright.
              const upright = angle > 90 || angle < -90 ? angle + 180 : angle;
              const isSelected = selectedWall === w.index;
              const isDragging = dragging?.kind === "wall" && dragging.index === w.index;
              const isLocked = w.index in lockedWallLengths;
              const label = formatLength(w.len, unit);
              const padX = 10;
              const charW = 7;
              const lockGutter = isLocked ? 16 : 0;
              const textWidth = Math.max(48, label.length * charW + padX * 2 + lockGutter);
              const active = isSelected || isDragging;
              // Locked but inactive walls get a distinct dark-amber pill so the
              // user can see at a glance which dimensions are pinned without
              // hijacking the selected-wall accent color.
              const bg = active
                ? ACCENT
                : isLocked
                  ? "#1f1813"
                  : "#0c0c10";
              return (
                <Group
                  key={`lbl-${w.index}`}
                  x={m.x}
                  y={m.y}
                  rotation={upright}
                  scaleX={labelK}
                  scaleY={labelK}
                  onMouseDown={(e) => startWallDrag(e, w.index)}
                  onTouchStart={(e) =>
                    pressTarget(
                      e,
                      { key: `wall-${w.index}`, selected: isSelected, select: () => selectRoomWall(w.index) },
                      (ev) => startWallDrag(ev, w.index)
                    )
                  }
                  onMouseEnter={(e) => {
                    const stage = e.target.getStage();
                    if (stage) stage.container().style.cursor = "pointer";
                  }}
                  onMouseLeave={(e) => {
                    const stage = e.target.getStage();
                    if (stage && !dragRef.current) stage.container().style.cursor = "default";
                  }}
                >
                  <Rect
                    x={-textWidth / 2}
                    y={-13}
                    width={textWidth}
                    height={26}
                    fill={bg}
                    cornerRadius={13}
                    stroke={isLocked && !active ? "#f59e0b" : undefined}
                    strokeWidth={isLocked && !active ? 1.5 : 0}
                    shadowColor="#000"
                    shadowBlur={6}
                    shadowOpacity={0.18}
                    shadowOffsetY={2}
                  />
                  {isLocked && (
                    <Group
                      x={-textWidth / 2 + 8}
                      y={-5}
                      scaleX={10 / 24}
                      scaleY={10 / 24}
                      listening={false}
                    >
                      <Path
                        data={LOCK_ICON_PATH}
                        stroke={active ? "#ffffff" : "#fbbf24"}
                        strokeWidth={2.5}
                        fill={undefined}
                        lineCap="round"
                        lineJoin="round"
                      />
                    </Group>
                  )}
                  <Text
                    text={label}
                    x={-textWidth / 2 + lockGutter}
                    y={-7}
                    width={textWidth - lockGutter}
                    align="center"
                    fontSize={13}
                    fontStyle="600"
                    fontFamily="Inter, sans-serif"
                    fill="#ffffff"
                    listening={false}
                  />
                </Group>
              );
            })}

            {/* Interior walls */}
            {interior.map((w) => {
              const a = fit.pointToPx(w.a);
              const b = fit.pointToPx(w.b);
              const selected =
                selectedInteriorWall != null &&
                selectedInteriorWall.wallId === w.wallId &&
                selectedInteriorWall.segment === w.segment;
              const isDragging =
                dragging?.kind === "interior-wall" &&
                dragging.wallId === w.wallId &&
                dragging.segment === w.segment;
              const active = selected || isDragging;
              const locked =
                !!w.wallId &&
                w.segment != null &&
                interiorLockKey(w.wallId, w.segment) in
                  (room.lockedInteriorLengths ?? {});
              const label = formatLength(w.len, unit);
              const textWidth = Math.max(48, label.length * 7 + 20);
              const mid = fit.pointToPx({
                x: (w.a.x + w.b.x) / 2,
                y: (w.a.y + w.b.y) / 2,
              });
              const angle = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
              const upright = angle > 90 || angle < -90 ? angle + 180 : angle;
              return (
                <Group key={`iw-${w.wallId}-${w.segment}`}>
                  <Line
                    points={[a.x, a.y, b.x, b.y]}
                    stroke="#0c0c10"
                    strokeWidth={WALL_THICKNESS_PX}
                    lineCap="square"
                    listening={false}
                  />
                  <Line
                    points={[a.x, a.y, b.x, b.y]}
                    stroke={active ? ACCENT : "rgba(0,0,0,0.001)"}
                    strokeWidth={active ? WALL_THICKNESS_PX + 4 : WALL_THICKNESS_PX}
                    hitStrokeWidth={WALL_HIT_PX}
                    lineCap="round"
                    opacity={active ? 0.55 : 1}
                    onMouseDown={(e) =>
                      startInteriorWallDrag(e, w.wallId!, w.segment!, w.a, w.b)
                    }
                    onTouchStart={(e) =>
                      pressTarget(
                        e,
                        {
                          key: `iw-${w.wallId}-${w.segment}`,
                          selected,
                          select: () => selectInteriorWall({ wallId: w.wallId!, segment: w.segment! }),
                        },
                        (ev) => startInteriorWallDrag(ev, w.wallId!, w.segment!, w.a, w.b)
                      )
                    }
                  />
                  <Group
                    x={mid.x}
                    y={mid.y}
                    rotation={upright}
                    scaleX={labelK}
                    scaleY={labelK}
                    onMouseDown={(e) =>
                      startInteriorWallDrag(e, w.wallId!, w.segment!, w.a, w.b)
                    }
                    onTouchStart={(e) =>
                      pressTarget(
                        e,
                        {
                          key: `iw-${w.wallId}-${w.segment}`,
                          selected,
                          select: () => selectInteriorWall({ wallId: w.wallId!, segment: w.segment! }),
                        },
                        (ev) => startInteriorWallDrag(ev, w.wallId!, w.segment!, w.a, w.b)
                      )
                    }
                  >
                    <Rect
                      x={-textWidth / 2}
                      y={-13}
                      width={textWidth}
                      height={26}
                      fill={active ? ACCENT : locked ? "#1f1813" : "#0c0c10"}
                      cornerRadius={13}
                      stroke={locked && !active ? "#f59e0b" : undefined}
                      strokeWidth={locked && !active ? 1.5 : 0}
                    />
                    <Text
                      text={label}
                      x={-textWidth / 2}
                      y={-7}
                      width={textWidth}
                      align="center"
                      fontSize={13}
                      fontStyle="600"
                      fontFamily="Inter, sans-serif"
                      fill="#ffffff"
                      listening={false}
                    />
                  </Group>
                </Group>
              );
            })}

            {/* Vertex handles (drawn last so they sit above walls) */}
            {hasRoomShape && points.map((p, i) => {
              const pp = fit.pointToPx(p);
              const isSelected = selectedVertex === i;
              const isDragging = dragging?.kind === "vertex" && dragging.index === i;
              const active = isSelected || isDragging;
              return (
                <Group
                  key={`v-${i}`}
                  x={pp.x}
                  y={pp.y}
                  onMouseDown={(e) => startVertexDrag(e, i)}
                  onTouchStart={(e) =>
                    pressTarget(
                      e,
                      { key: `v-${i}`, selected: isSelected, select: () => selectRoomVertex(i) },
                      (ev) => startVertexDrag(ev, i)
                    )
                  }
                  onMouseEnter={(e) => {
                    const stage = e.target.getStage();
                    if (stage) stage.container().style.cursor = "grab";
                  }}
                  onMouseLeave={(e) => {
                    const stage = e.target.getStage();
                    if (stage && !dragRef.current) stage.container().style.cursor = "default";
                  }}
                >
                  {/* Halo while active for visual feedback */}
                  {active && (
                    <Circle radius={VERTEX_RADIUS_PX + 10} fill={`${ACCENT}22`} listening={false} />
                  )}
                  {/* Invisible touch target */}
                  <Circle radius={VERTEX_HIT_PX} opacity={0} />
                  {/* Outer ring */}
                  <Circle
                    radius={VERTEX_RADIUS_PX}
                    fill="#ffffff"
                    stroke={active ? ACCENT : "#0c0c10"}
                    strokeWidth={active ? 3 : 2.5}
                    shadowColor="#000"
                    shadowBlur={active ? 10 : 6}
                    shadowOpacity={active ? 0.25 : 0.15}
                    shadowOffsetY={2}
                    listening={false}
                  />
                  {/* Inner dot */}
                  <Circle
                    radius={4}
                    fill={active ? ACCENT : "#0c0c10"}
                    listening={false}
                  />
                </Group>
              );
            })}

            {interiorOnlyVertices.map((v) => {
              const pp = fit.pointToPx(v);
              const isSelected = selectedInteriorVertexId === v.id;
              const isDragging =
                dragging?.kind === "interior-vertex" && dragging.vertexId === v.id;
              const active = isSelected || isDragging;
              return (
                <Group
                  key={`iv-${v.id}`}
                  x={pp.x}
                  y={pp.y}
                  onMouseDown={(e) => startInteriorVertexDrag(e, v.id, v)}
                  onTouchStart={(e) =>
                    pressTarget(
                      e,
                      { key: `iv-${v.id}`, selected: isSelected, select: () => selectInteriorVertex(v.id) },
                      (ev) => startInteriorVertexDrag(ev, v.id, v)
                    )
                  }
                >
                  {active && (
                    <Circle radius={VERTEX_RADIUS_PX + 10} fill={`${ACCENT}22`} listening={false} />
                  )}
                  <Circle radius={VERTEX_HIT_PX} opacity={0} />
                  <Circle
                    radius={VERTEX_RADIUS_PX}
                    fill="#ffffff"
                    stroke={active ? ACCENT : "#0c0c10"}
                    strokeWidth={active ? 3 : 2.5}
                    listening={false}
                  />
                  <Circle radius={4} fill={active ? ACCENT : "#0c0c10"} listening={false} />
                </Group>
              );
            })}
          </Layer>

          {/* Collaborator selection halos (build view: highlight selected
              walls / vertices in the collaborator's color). Mounted before
              the cursor layer so cursors render on top of halos. */}
          <Layer listening={false}>
            <CollaboratorSelections
              collaborators={collaborators}
              furniture={[]}
              mounts={[]}
              openings={[]}
              roomPoints={points}
              fit={fit}
              view="build"
              activeWallIndex={null}
              stageScale={panZoom.scale}
            />
            <CollaboratorDragPreviewsBuild
              collaborators={collaborators}
              fit={fit}
            />
          </Layer>

          {/* Live collaborator cursors. */}
          <Layer listening={false}>
            <CollaboratorCursors
              collaborators={collaborators}
              fit={fit}
              roomPoints={points}
              view="build"
              activeWallIndex={null}
              wallLengthIn={null}
              viewport={size}
              stage={{ scale: panZoom.scale, x: panZoom.x, y: panZoom.y }}
            />
          </Layer>

          {/* Ghost preview of the cleaned sketch polygon awaiting confirm. */}
          {previewFlat && (
            <Layer listening={false}>
              <Line
                points={previewFlat}
                closed={previewClosed}
                stroke="#2a70f5"
                strokeWidth={4}
                dash={[10, 6]}
                opacity={0.85}
                lineJoin="round"
                lineCap="round"
              />
            </Layer>
          )}

          {/* Sketch tool capture layer (sits on top of everything). The `key`
              bound to `panMode` remounts the overlay whenever Space-to-pan
              flips on/off, which is the cleanest way to abandon any
              half-drawn stroke without setting state from an effect. */}
          {sketchActive && onSketchResult && (
            <SketchOverlay
              key={panMode ? "pan" : "draw"}
              width={size.width}
              height={size.height}
              view={{ x: panZoom.x, y: panZoom.y, scale: panZoom.scale }}
              fit={fit}
              roomPoints={points}
              extendMode={extendMode}
              room={room}
              openings={openings}
              mounts={mounts}
              panMode={panMode}
              onResult={onSketchResult}
            />
          )}

        </Stage>
      )}

      {/* Drag hint: a screen-fixed HUD so it doesn't drift or scale with pan/zoom. */}
      {!hideAreaBadge && dragging && (
        <div className="pointer-events-none absolute inset-x-0 top-3.5 z-10 flex justify-center">
          <div className="rounded-full bg-accent-500/95 px-3.5 py-1.5 text-caption font-semibold text-white shadow-sm">
            {dragging.kind === "wall"
              ? "Slide wall to resize the room"
              : shiftHeldRef.current
                ? "Axis locked — release Shift to free up"
                : "Drag corner — hold Shift to lock to an axis"}
          </div>
        </div>
      )}

      {panZoom.isAdjusted && (
        <button
          onClick={() => {
            setFitBasisPoints(points.map((p) => ({ ...p })));
            panZoom.reset();
          }}
          aria-label="Recenter"
          className="absolute right-[calc(0.75rem+var(--safe-right))] bottom-[var(--canvas-overlay-bottom)] z-10 inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white ring-1 ring-ink-200 text-xs font-medium text-ink-700 shadow-sm hover:bg-ink-50 active:bg-ink-100 transition-colors"
        >
          <Locate className="size-3.5" />
          Recenter
        </button>
      )}

      <GridLegend step={gridStep} unit={unit} />
      <HoldHint show={pickup.holdHint} label="Hold a wall or corner to move it" />
    </div>
  );
}
